import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CloseConfirmationDialog from '@/shared/components/dialogs/CloseConfirmationDialog';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import ShippingProfileVariant from '@/shared/types/shipping/shippingProfileVariant.type';

import useFetchShippingProviders from '../hooks/useFetchShippingProviders';
import useFetchTemplateSchemas from '../hooks/useFetchTemplateSchemas';
import CreateEditShippingProfileDialog from './components/CreateEditShippingProfileDialog/CreateEditShippingProfileDialog';
import ShippingProfilesTable_v2 from './components/ShippingProfilesTable_v2/ShippingProfilesTable_v2';
import Title from './components/Title';
import useShippingProfilesOperations from './hooks/useShippingProfilesOperations';

const ShippingProfiles: FC = () => {
  const { t } = useTranslation(['shipping']);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { shippingProviders } = useFetchShippingProviders();
  const { templateSchemas } = useFetchTemplateSchemas();

  const {
    refreshTableIndex,
    handleProfileCreate,
    handleAssignProductsAndVariants,
  } = useShippingProfilesOperations({
    onUpdateDialogState,
    onUpdateProgressDialog,
    onUpdateNotificationBar,
  });

  return (
    <div>
      <Title />

      <ShippingProfilesTable_v2
        refreshTableIndex={refreshTableIndex}
        onCreateShippingProfile={() => {
          onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
            open: true,
            errors: [],
            data: null,
          });
        }}
        onAssignProductsAndVariants={(profiles) => {
          onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
            open: true,
            data: profiles,
            errors: [],
          });
        }}
      />

      {dialog?.createEditShippingProfileDialog?.open &&
        shippingProviders &&
        templateSchemas && (
          <CreateEditShippingProfileDialog
            initialView={
              (
                dialog?.createEditShippingProfileDialog as {
                  data?: ShippingProfile[];
                }
              )?.data
                ? 'product_variants'
                : undefined
            }
            shippingProviders={shippingProviders}
            templateSchemas={templateSchemas}
            errors={dialog?.createEditShippingProfileDialog?.errors}
            onClose={() => {
              onUpdateDialogState(DialogName.CloseConfirmationDialog, {
                open: true,
              });
            }}
            proceed={(value) => {
              const { data } = dialog?.createEditShippingProfileDialog as {
                data?: ShippingProfile[];
              };

              if (data && data.length > 0) {
                void handleAssignProductsAndVariants(
                  data,
                  value.profile
                    .product_variants as unknown as ShippingProfileVariant[],
                );
              } else {
                void handleProfileCreate(value);
              }
            }}
          />
        )}

      {dialog?.closeConfirmationDialog?.open && (
        <CloseConfirmationDialog
          title={t('shipping:confirmCloseAction')}
          text={t('shipping:confirmCloseActionText')}
          onClose={() => {
            onUpdateDialogState(DialogName.CloseConfirmationDialog, {
              open: false,
            });
          }}
          proceed={() => {
            onUpdateDialogState(DialogName.CloseConfirmationDialog, {
              open: false,
            });
            onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
              open: false,
            });
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
      />
    </div>
  );
};

export default ShippingProfiles;
